import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { useTenantProfileUpload } from './TenantProfileUploadProvider';

/** True when the drag carries files (vs. e.g. dragging selected page text). */
function dragHasFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Makes its whole subtree a drop target: drop a file anywhere on the page and it
 * is handed straight to the upload provider (→ auto-classify → auto-extract). A
 * dragenter/dragleave counter avoids the flicker you get when the drag moves
 * between child elements — the overlay only hides once the pointer truly leaves
 * the page. Dispatches the same `addFiles` the browse button uses.
 */
export const PageUploadDropzone: FC<{ children: ReactNode }> = observer(
  ({ children }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const { addFiles } = useTenantProfileUpload();
    const [isDragging, setIsDragging] = useState(false);
    const dragDepth = useRef(0);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) addFiles(files);
      },
      [addFiles],
    );

    return (
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{ position: 'relative', height: '100%', minHeight: 0 }}
      >
        {children}

        {isDragging && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: 4,
              textAlign: 'center',
              pointerEvents: 'none',
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              backdropFilter: 'blur(2px)',
              border: `2px dashed ${theme.palette.primary.main}`,
              borderRadius: 3,
            }}
            aria-live="polite"
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="textLG_bold" color="primary.main">
              {t('app.orgProfile.upload.dropOverlay')}
            </Typography>
            <Typography variant="textSM_regular" color="text.secondary">
              {t('app.orgProfile.upload.dropOverlayHint')}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);
